import type * as zod from 'zod';

import type { CartesianPose } from '@sb/geometry';
import { namespace, info } from '@sb/log';
import type { ArmTarget } from '@sb/motion-planning';
import type { SpeedProfile } from '@sb/routine-runner/speed-profile';
import type { NonEmptyArray } from '@sb/utilities/src/types';

import type { PushModeStep } from '..';
import type { RoutineContext } from '../../RoutineContext';
import type ArcStep from '../Arc/Step';
import type { StepPlayArguments } from '../Step';
import Step from '../Step';
import type WaypointStep from '../Waypoint/Step';

import Arguments from './Arguments';
import Variables from './Variables';

type Arguments = zod.infer<typeof Arguments>;

type Variables = zod.infer<typeof Variables>;

const ns = namespace('MoveArmToStepV2');

export default class MoveArmToV2Step extends Step<Arguments, Variables> {
  public static areSubstepsRequired = true;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public substeps: Array<Step<object, object>> = [];

  public initializeVariableState(): void {
    const { completedCount = 0 } = this.variablesForInitialization;

    this.variables = {
      completedCount,
      currentActivity: 'none',
    };
  }

  private motion?: ReturnType<RoutineContext['doMotion']>;

  public async _play({
    fail,
    pushMode,
    hasEncounteredStartStep,
    pauseRoutine,
  }: StepPlayArguments): Promise<void> {
    if (!hasEncounteredStartStep()) {
      // If we haven't encountered the start step yet, we don't need to play
      info(
        `MoveArmToV2Step._play`,
        `Skipping MoveArmToV2Step because we haven't reached the start step`,
      );

      return;
    }

    if (this.variables.currentActivity !== 'none') {
      // cannot play unless the step is doing nothing currently
      throw new Error(
        `cannot double play MoveArmToV2Step (current activity: ${this.variables.currentActivity})`,
      );
    }

    info(`MoveArmToV2Step._play`, `playing MoveArmToV2Step`);

    const targets: ArmTarget[] = [];

    const speedProfiles: SpeedProfile[] = [];

    const { kinematicState } = this.routineContext.getRoutineRunnerState();

    let previousTargetPose: CartesianPose =
      kinematicState.tcpOffsetOption === 'wrist'
        ? kinematicState.wristPose
        : kinematicState.tooltipPoint;

    // Process waypoints first
    for (const substep of this.substeps) {
      const childStep = substep as WaypointStep | ArcStep;

      // Pass the parent's completedCount to support position list progression
      const result = await childStep.getArmTarget({
        parentCompletedCount: this.variables.completedCount,
        previousTargetPose,
      });

      if ('failure' in result) {
        fail(result);

        return;
      }

      const { target, poseForPreviousTarget } = result as {
        target: ArmTarget;
        poseForPreviousTarget: CartesianPose;
      };

      previousTargetPose = poseForPreviousTarget;

      // Apply blend configuration based on parent config
      if (
        'useParentBlendConfig' in childStep.args &&
        childStep.args.useParentBlendConfig
      ) {
        target.blendRadius = this.args.blendConfig.radius;
      }

      // Since per-waypoint speed profiles are not yet supported, we use the parent speed profile.
      speedProfiles.push(this.args.speedProfile);

      targets.push(target);
    }

    let hasPushed = false;

    if (pushMode) {
      info(ns`_play`, `checking push mode`);

      for (const parentStep of this.parentSteps) {
        if (parentStep.getStepKind() === 'PushMode') {
          const pushModeStep = parentStep as PushModeStep;

          if (pushModeStep.hasPushed) {
            hasPushed = true;
          }

          pushModeStep.hasPushed = true;
        }
      }
    }

    const motion = this.routineContext.doMotion({
      request: {
        targets: targets as NonEmptyArray<ArmTarget>,
      },
      speedProfiles,
      pushUntilCollision: pushMode && !hasPushed,
      pushMode,
      stepID: this.id,
    });

    motion.on('requestingPlan', () => {
      if (this.variables.currentActivity === 'none') {
        this.setVariable('currentActivity', 'requestingPlan');
      }
    });

    motion.on('planning', () => {
      if (this.variables.currentActivity === 'requestingPlan') {
        this.setVariable('currentActivity', 'planning');
      }
    });

    motion.on('beginMotion', () => {
      this.setVariable('currentActivity', 'moving');
    });

    motion.on('pause', () => {
      this.setVariable('currentActivity', 'paused');
    });

    // Handle preflight test run completion when first waypoint is reached
    motion.once('waypointReached', () => {
      const routineRunnerState = this.routineContext.getRoutineRunnerState();

      if (
        routineRunnerState.kind === 'RoutineRunning' &&
        routineRunnerState.isPreflightTestRun
      ) {
        // Set isPreflightTestRun to false
        this.routineContext.setRoutineRunnerState({
          ...routineRunnerState,
          isPreflightTestRun: false,
        });

        // Check if this was the only waypoint (single waypoint motion)
        // If so, delay the pause until after motion completion
        if (targets.length === 1) {
          // Listen for motion completion, then trigger pause
          motion.once('complete', () => {
            pauseRoutine({
              kind: 'preflightTestRunCompleted',
              reason: 'The jogging motion completed.',
            });
          });

          return;
        }

        // For multiple waypoints, pause after the first one
        // Trigger pause for preflight test run completion
        pauseRoutine({
          kind: 'preflightTestRunCompleted',
          reason: 'The jogging motion completed.',
        });
      }
    });

    motion.on('complete', () => {
      this.setVariable('completedCount', this.variables.completedCount + 1);
    });

    motion.on('cancelled', () => {
      this.setVariable('currentActivity', 'none');
    });

    motion.on('failure', (failure) => fail(failure));

    this.motion = motion;

    try {
      await motion.race('failure', 'complete', 'cancelled');
    } finally {
      // Clean up regardless of how the motion ended
      this.setVariable('currentActivity', 'none');
      motion.removeAllListeners();

      delete this.motion;
    }
  }

  public _pause(): void {
    if (this.motion) {
      this.motion.emit('pause');
    }
  }

  public _resume(): void {
    if (this.motion) {
      this.motion.emit('resume');
    }
  }

  public _stop() {
    if (this.motion) {
      this.motion.emit('cancel');
    }
  }

  public hasSelfTimeout(): boolean {
    return false;
  }
}
